import { useMemo, useState } from "react";
import FontAwesome from "../../utilities/FontAwesome";
import styles from "./styles/MessagingListRules.module.scss";
import {
  displayGrade,
  IRecipientsType,
  MessagingListQueryOptions,
  MessagingListRulesQuery,
} from "./MessagingListRules";
import { useStores } from "../../../stores/useStores";
import { useTranslation } from "react-i18next";
import i18n from "../../../internationalization/i18n";
import { Badge, Box, Button, Flex, Select } from "@radix-ui/themes";
import useTheme from "../../radix/useTheme";
import { RuleOrganizationDropdown } from "./RuleOrganizationDropdown";
import { IMessagingListModalIntent } from "./AddOrEditMessagingListModal";

export interface IMessageListRuleTag {
  value: string;
  filter?: string;
  type: MessagingListQueryOptions;
}

export enum QueryBuilderGradeTypes {
  KG = "KG",
  FIRST_GRADE = "01",
  SECOND_GRADE = "02",
  THIRD_GRADE = "03",
  FOURTH_GRADE = "04",
  FIFTH_GRADE = "05",
  SIXTH_GRADE = "06",
  SEVENTH_GRADE = "07",
  EIGHTH_GRADE = "08",
  NINTH_GRADE = "09",
  TENTH_GRADE = "10",
  ELEVENTH_GRADE = "11",
  TWELFTH_GRADE = "12",
}

export const gradeTypeToValueMap: { [value: string]: string } = {
  [QueryBuilderGradeTypes.KG]: "Kindergarten",
  [QueryBuilderGradeTypes.FIRST_GRADE]: "1st Grade",
  [QueryBuilderGradeTypes.SECOND_GRADE]: "2nd Grade",
  [QueryBuilderGradeTypes.THIRD_GRADE]: "3rd Grade",
  [QueryBuilderGradeTypes.FOURTH_GRADE]: "4th Grade",
  [QueryBuilderGradeTypes.FIFTH_GRADE]: "5th Grade",
  [QueryBuilderGradeTypes.SIXTH_GRADE]: "6th Grade",
  [QueryBuilderGradeTypes.SEVENTH_GRADE]: "7th Grade",
  [QueryBuilderGradeTypes.EIGHTH_GRADE]: "8th Grade",
  [QueryBuilderGradeTypes.NINTH_GRADE]: "9th Grade",
  [QueryBuilderGradeTypes.TENTH_GRADE]: "10th Grade",
  [QueryBuilderGradeTypes.ELEVENTH_GRADE]: "11th Grade",
  [QueryBuilderGradeTypes.TWELFTH_GRADE]: "12th Grade",
};

export enum QueryBuilderGroupTypes {
  STAFF = "staff",
  SUBSCRIBER = "follower",
  TEACHER = "teacher",
  STUDENT = "student",
  GUARDIAN = "guardian",
}

export const groupTypeToValueMap: { [value: string]: string } = {
  [QueryBuilderGroupTypes.STAFF]: "Staff",
  [QueryBuilderGroupTypes.SUBSCRIBER]: "Subscribers",
  [QueryBuilderGroupTypes.TEACHER]: "Teachers",
  [QueryBuilderGroupTypes.STUDENT]: "Students",
  [QueryBuilderGroupTypes.GUARDIAN]: "Parents / Guardian",
};

export function MessagingListRuleQueryBuilder(props: {
  addRule: (rule: MessagingListRulesQuery) => void;
  setRecipients: (recipients: IUser[]) => void;
  messageIntent: "broadcast" | "message";
  modalIntent: IMessagingListModalIntent;
  recipientsType: IRecipientsType;
}) {
  const radixTheme = useTheme();

  const { organizationStore, modalStore, userStore } = useStores();

  const { t } = useTranslation("translation", { i18n });

  // Translated maps
  const translatedGradeTypeToValueMap: { [value: string]: string } = {
    [QueryBuilderGradeTypes.KG]: t("Kindergarten"),
    [QueryBuilderGradeTypes.FIRST_GRADE]: t("1st Grade"),
    [QueryBuilderGradeTypes.SECOND_GRADE]: t("2nd Grade"),
    [QueryBuilderGradeTypes.THIRD_GRADE]: t("3rd Grade"),
    [QueryBuilderGradeTypes.FOURTH_GRADE]: t("4th Grade"),
    [QueryBuilderGradeTypes.FIFTH_GRADE]: t("5th Grade"),
    [QueryBuilderGradeTypes.SIXTH_GRADE]: t("6th Grade"),
    [QueryBuilderGradeTypes.SEVENTH_GRADE]: t("7th Grade"),
    [QueryBuilderGradeTypes.EIGHTH_GRADE]: t("8th Grade"),
    [QueryBuilderGradeTypes.NINTH_GRADE]: t("9th Grade"),
    [QueryBuilderGradeTypes.TENTH_GRADE]: t("10th Grade"),
    [QueryBuilderGradeTypes.ELEVENTH_GRADE]: t("11th Grade"),
    [QueryBuilderGradeTypes.TWELFTH_GRADE]: t("12th Grade"),
  };

  const translatedGroupTypeToValueMap: { [value: string]: string } = {
    [QueryBuilderGroupTypes.STAFF]: t("Staff"),
    [QueryBuilderGroupTypes.SUBSCRIBER]: t("Subscribers"),
    [QueryBuilderGroupTypes.TEACHER]: t("Teachers"),
    [QueryBuilderGroupTypes.STUDENT]: t("Students"),
    [QueryBuilderGroupTypes.GUARDIAN]: t("Parents / Guardian"),
  };

  // Data for dropdown options.
  const grades = [
    {
      value: t("All Grades"),
      type: MessagingListQueryOptions.Grade,
      filter: undefined,
    },
    {
      value: translatedGradeTypeToValueMap[QueryBuilderGradeTypes.KG],
      type: MessagingListQueryOptions.Grade,
      filter: QueryBuilderGradeTypes.KG,
    },
    {
      value: translatedGradeTypeToValueMap[QueryBuilderGradeTypes.FIRST_GRADE],
      type: MessagingListQueryOptions.Grade,
      filter: QueryBuilderGradeTypes.FIRST_GRADE,
    },
    {
      value: translatedGradeTypeToValueMap[QueryBuilderGradeTypes.SECOND_GRADE],
      type: MessagingListQueryOptions.Grade,
      filter: QueryBuilderGradeTypes.SECOND_GRADE,
    },
    {
      value: translatedGradeTypeToValueMap[QueryBuilderGradeTypes.THIRD_GRADE],
      type: MessagingListQueryOptions.Grade,
      filter: QueryBuilderGradeTypes.THIRD_GRADE,
    },
    {
      value: translatedGradeTypeToValueMap[QueryBuilderGradeTypes.FOURTH_GRADE],
      type: MessagingListQueryOptions.Grade,
      filter: QueryBuilderGradeTypes.FOURTH_GRADE,
    },
    {
      value: translatedGradeTypeToValueMap[QueryBuilderGradeTypes.FIFTH_GRADE],
      type: MessagingListQueryOptions.Grade,
      filter: QueryBuilderGradeTypes.FIFTH_GRADE,
    },
    {
      value: translatedGradeTypeToValueMap[QueryBuilderGradeTypes.SIXTH_GRADE],
      type: MessagingListQueryOptions.Grade,
      filter: QueryBuilderGradeTypes.SIXTH_GRADE,
    },
    {
      value:
        translatedGradeTypeToValueMap[QueryBuilderGradeTypes.SEVENTH_GRADE],
      type: MessagingListQueryOptions.Grade,
      filter: QueryBuilderGradeTypes.SEVENTH_GRADE,
    },
    {
      value: translatedGradeTypeToValueMap[QueryBuilderGradeTypes.EIGHTH_GRADE],
      type: MessagingListQueryOptions.Grade,
      filter: QueryBuilderGradeTypes.EIGHTH_GRADE,
    },
    {
      value: translatedGradeTypeToValueMap[QueryBuilderGradeTypes.NINTH_GRADE],
      type: MessagingListQueryOptions.Grade,
      filter: QueryBuilderGradeTypes.NINTH_GRADE,
    },
    {
      value: translatedGradeTypeToValueMap[QueryBuilderGradeTypes.TENTH_GRADE],
      type: MessagingListQueryOptions.Grade,
      filter: QueryBuilderGradeTypes.TENTH_GRADE,
    },
    {
      value:
        translatedGradeTypeToValueMap[QueryBuilderGradeTypes.ELEVENTH_GRADE],
      type: MessagingListQueryOptions.Grade,
      filter: QueryBuilderGradeTypes.ELEVENTH_GRADE,
    },
    {
      value:
        translatedGradeTypeToValueMap[QueryBuilderGradeTypes.TWELFTH_GRADE],
      type: MessagingListQueryOptions.Grade,
      filter: QueryBuilderGradeTypes.TWELFTH_GRADE,
    },
  ];

  const staffRole = {
    value: translatedGroupTypeToValueMap[QueryBuilderGroupTypes.STAFF],
    type: MessagingListQueryOptions.Group,
    filter: QueryBuilderGroupTypes.STAFF,
    showSISIcon: false,
  };

  const groups = useMemo(() => {
    const g: Array<{
      value: string;
      type: MessagingListQueryOptions;
      filter: QueryBuilderGroupTypes;
      showSISIcon: boolean;
    }> = [
      staffRole,
      {
        value: translatedGroupTypeToValueMap[QueryBuilderGroupTypes.TEACHER],
        type: MessagingListQueryOptions.Group,
        filter: QueryBuilderGroupTypes.TEACHER,
        showSISIcon: false,
      },
    ];

    if (organizationStore.organization.user_roles.is_staff) {
      g.push({
        value: translatedGroupTypeToValueMap[QueryBuilderGroupTypes.STUDENT],
        type: MessagingListQueryOptions.Group,
        filter: QueryBuilderGroupTypes.STUDENT,
        showSISIcon: !organizationStore.currentOrganization.oneroster_id,
      });
      g.push({
        value: translatedGroupTypeToValueMap[QueryBuilderGroupTypes.GUARDIAN],
        type: MessagingListQueryOptions.Group,
        filter: QueryBuilderGroupTypes.GUARDIAN,
        showSISIcon: !organizationStore.currentOrganization.oneroster_id,
      });
    }

    if (userStore.admin) {
      g.push({
        value: translatedGroupTypeToValueMap[QueryBuilderGroupTypes.SUBSCRIBER],
        type: MessagingListQueryOptions.Group,
        filter: QueryBuilderGroupTypes.SUBSCRIBER,
        showSISIcon: false,
      });
    }
    return g;
  }, []);

  const [role, setRole] = useState<IMessageListRuleTag | undefined>(undefined);
  const [grade, setGrade] = useState<IMessageListRuleTag | undefined>(
    undefined,
  );
  const [organization, setOrganization] = useState<IMessageListRuleTag | null>(
    null,
  );

  const addRuleDisabled: boolean =
    role && organization && displayGrade(role, organization)
      ? !(role?.value && grade?.value && organization?.value)
      : !(role?.value && organization?.value);

  return (
    <Box py={"1"}>
      <Flex gapX={"2"} align={"center"} justify={"start"} wrap={"nowrap"}>
        <Select.Root
          value={JSON.stringify(role)}
          onValueChange={(value) => {
            const newRole = JSON.parse(value);
            if (organization) {
              setGrade(
                displayGrade(newRole, organization) ? grades[0] : undefined,
              );
            }
            setRole(newRole);
          }}
        >
          <Select.Trigger placeholder={"Select Role"} />
          <Select.Content
            container={radixTheme.portalContainer?.current}
            position={"popper"}
          >
            {groups.map((group) => (
              <Select.Item key={group.value} value={JSON.stringify(group)}>
                <Box>
                  {group.value}{" "}
                  {group.showSISIcon && (
                    <Badge color={"blue"}>{t("SIS Only")}</Badge>
                  )}
                </Box>
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
        <Box>
          <Badge>AT</Badge>
        </Box>
        <Box flexShrink={"1"} className={styles.organizationSelectionButton}>
          <RuleOrganizationDropdown
            organization={organization}
            setOrganization={(orgRule: IMessageListRuleTag) => {
              setOrganization(orgRule);
              if (role) {
                setGrade(displayGrade(role, orgRule) ? grades[0] : undefined);
              }
            }}
            modalIntent={props.modalIntent}
          />
        </Box>
        {role && organization && displayGrade(role, organization) && (
          <>
            <Box>
              <Badge>OF</Badge>
            </Box>
            <Select.Root
              value={JSON.stringify(grade)}
              onValueChange={(value) => setGrade(JSON.parse(value))}
            >
              <Select.Trigger placeholder={"Select Grade"} />
              <Select.Content
                container={radixTheme.portalContainer?.current}
                position={"popper"}
              >
                {grades.map((grade) => (
                  <Select.Item key={grade.value} value={JSON.stringify(grade)}>
                    {grade.value}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </>
        )}
      </Flex>
      <Box pt={"2"}>
        {props.recipientsType === "individuals" && (
          <Button
            style={{ marginRight: "5px" }}
            variant={"outline"}
            disabled={addRuleDisabled}
            onClick={() => {
              modalStore.addModal({
                type: "peopleSelection",
                filterRole: (role as IMessageListRuleTag).filter,
                filterRoleOrganizationId: (organization as IMessageListRuleTag)
                  .filter,
                selectRecipients: props.setRecipients,
              });
            }}
          >
            <FontAwesome prefix={"fas"} name={"fa-user"} /> Select Individuals
          </Button>
        )}
        {props.recipientsType === "groups" && (
          <Button
            variant={"outline"}
            disabled={addRuleDisabled}
            onClick={() => {
              props.addRule({
                [MessagingListQueryOptions.Group]: role as IMessageListRuleTag,
                [MessagingListQueryOptions.Grade]: grade,
                [MessagingListQueryOptions.Organization]:
                  organization as IMessageListRuleTag,
                [MessagingListQueryOptions.Operator]: {
                  value: "OR",
                  type: MessagingListQueryOptions.Operator,
                  filter: "or",
                },
              });
              setRole(groups[0]);
              setGrade(undefined);
              setOrganization(null);
            }}
          >
            <FontAwesome prefix={"fas"} name={"fa-users"} />
            &nbsp;Add Group
          </Button>
        )}
      </Box>
    </Box>
  );
}
